// Iter 92 — Clip-request In/Out marker (public ShareView). Frame-accurate:
// In/Out are whole frames at the asset's frame rate (assumed 23.976 when
// unknown), shown and editable as HH:MM:SS:FF (drop-frame ;FF for 29.97/59.94).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Scissors } from "lucide-react";
import { resolveFps, fpsLabel, frameToTc, tcToFrame, frameToSec, playheadFrame } from "@/lib/timecode";

function TcField({ label, testId, frame, fps, maxFrame, onCommit, onCapture, captureLabel }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { setDraft(frame == null ? "" : frameToTc(frame, fps)); setError(""); }, [frame, fps]);

  const commit = () => {
    if (!draft.trim()) { setError(""); return; }
    const r = tcToFrame(draft, fps);
    if (r.error) return setError(r.error);
    if (maxFrame != null && r.frame > maxFrame) return setError(`Past the end (${frameToTc(maxFrame, fps)})`);
    setError("");
    onCommit(r.frame);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          data-testid={`${testId}`}
          onClick={onCapture}
          className="shrink-0 bg-black/40 hover:bg-black/70 border border-zinc-800 hover:border-[var(--brand-accent)]/60 rounded-full px-3 py-1.5 text-xs text-zinc-400 transition-colors"
        >
          {captureLabel}
        </button>
        <input
          data-testid={`${testId}-input`}
          aria-label={`${label} timecode`}
          value={draft}
          placeholder={frameToTc(0, fps)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
          className={`w-[7.5rem] bg-black/40 border rounded-md px-2 py-1 text-xs tabular-nums text-[var(--brand-accent)] focus:outline-none ${error ? "border-red-700" : "border-zinc-800 focus:border-[var(--brand-accent)]/60"}`}
        />
      </div>
      {error && <div data-testid={`${testId}-error`} className="text-[10px] text-red-300">{error}</div>}
    </div>
  );
}

export default function ClipRequestMarker({ asset, playerRef, onAdd }) {
  const fr = resolveFps(asset.frame_rate);
  const { fps } = fr;
  const [inFrame, setInFrame] = useState(null);
  const [outFrame, setOutFrame] = useState(null);
  const [note, setNote] = useState("");
  const maxFrame = asset.duration_sec ? Math.ceil(asset.duration_sec * fps) : null;

  const capture = (set) => () => {
    const p = playerRef?.current;
    if (!p?.getCurrentTime) return;
    try {
      p.getCurrentTime((secs) => {
        if (typeof secs === "number" && isFinite(secs)) set(playheadFrame(secs, fps));
      });
    } catch { /* player not ready yet */ }
  };

  const typed = (set) => (frame) => {
    set(frame);
    try { playerRef?.current?.setCurrentTime?.(frameToSec(frame, fps)); } catch { /* player not ready */ }
  };

  const valid = inFrame != null && outFrame != null && outFrame > inFrame;

  const add = () => {
    if (!valid) return;
    onAdd({
      asset_id: asset.id,
      asset_title: asset.title || "(untitled)",
      in_sec: frameToSec(inFrame, fps),
      out_sec: frameToSec(outFrame, fps),
      in_frame: inFrame,
      out_frame: outFrame,
      frame_rate: fps,
      frame_rate_assumed: fr.assumed,
      note: note.trim(),
    });
    toast.success(`Clip queued · In ${frameToTc(inFrame, fps)} → Out ${frameToTc(outFrame, fps)}`);
    setInFrame(null); setOutFrame(null); setNote("");
  };

  return (
    <div data-testid="clip-request-marker" className="mt-3 bg-zinc-950/60 border border-zinc-800/60 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-zinc-500 flex-wrap">
        <Scissors className="w-3 h-3 text-[var(--brand-accent)]" />
        Mark a clip for the editing team
        <span data-testid="clip-fps-label" className="ml-auto normal-case tracking-normal text-[10px] text-zinc-500">{fpsLabel(fr)}</span>
      </div>
      <div className="flex flex-wrap gap-3 items-start">
        <TcField label="In" testId="clip-mark-in" captureLabel="◀ Mark In" frame={inFrame} fps={fps} maxFrame={maxFrame} onCommit={typed(setInFrame)} onCapture={capture(setInFrame)} />
        <TcField label="Out" testId="clip-mark-out" captureLabel="Mark Out ▶" frame={outFrame} fps={fps} maxFrame={maxFrame} onCommit={typed(setOutFrame)} onCapture={capture(setOutFrame)} />
        {inFrame != null && outFrame != null && (
          <span data-testid="clip-delta" className={`inline-flex items-center text-[11px] tabular-nums rounded-full px-2 py-1 ${valid ? "bg-[var(--brand-accent)]/15 text-[var(--brand-accent)] border border-[var(--brand-accent)]/40" : "bg-red-950/40 text-red-300 border border-red-900/60"}`}>
            {valid ? `${outFrame - inFrame} frames` : "Out must be after In"}
          </span>
        )}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 500))}
        placeholder="Note for the editor (optional, e.g. 'Start when they say X, end with Y')"
        data-testid="clip-note"
        rows={2}
        className="w-full bg-black/40 border border-zinc-800 rounded-md px-3 py-2 text-xs resize-none focus:outline-none focus:border-[var(--brand-accent)]/60"
      />
      <button
        type="button"
        disabled={!valid}
        onClick={add}
        data-testid="clip-add-to-tray"
        className="w-full flex items-center justify-center gap-2 bg-[var(--brand-accent)] text-black font-semibold rounded-md px-3 py-2 text-xs hover:bg-[var(--brand-accent)]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Scissors className="w-3.5 h-3.5" /> Add to request tray
      </button>
    </div>
  );
}
